import { createContext, useCallback, useContext, useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { SessionDto } from '@orienta/shared';
import { ApiError, api, setCsrfToken } from '@/lib/api';

interface SessionState {
  session: SessionDto | null;
  loading: boolean;
  error: unknown;
  setSession: (s: SessionDto | null) => void;
  refresh: () => Promise<void>;
  logout: () => Promise<void>;
}

const SessionContext = createContext<SessionState | null>(null);
export const SESSION_KEY = ['session'] as const;

async function fetchSession(): Promise<SessionDto | null> {
  try {
    return await api<SessionDto>('/auth/session', { skipAuthRedirect: true });
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) return null;
    throw err;
  }
}

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const qc = useQueryClient();
  const query = useQuery({ queryKey: SESSION_KEY, queryFn: fetchSession, staleTime: 5 * 60_000, retry: false });
  const session = query.data ?? null;

  useEffect(() => {
    setCsrfToken(session?.csrfToken ?? null);
  }, [session]);

  const setSession = useCallback(
    (s: SessionDto | null) => {
      setCsrfToken(s?.csrfToken ?? null);
      qc.setQueryData(SESSION_KEY, s);
    },
    [qc],
  );

  const refresh = useCallback(async () => {
    await qc.invalidateQueries({ queryKey: SESSION_KEY });
  }, [qc]);

  const logout = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST', body: {}, skipAuthRedirect: true });
    } finally {
      setCsrfToken(null);
      // Sessão vazia primeiro (RequireAuth leva ao login e a casca desmonta), depois o restante do cache.
      // qc.clear() deixava a consulta observada da sessão com a conta antiga: a tela seguia logada e as
      // consultas da casca repetiam 401 até o limite de requisições (429 "Muitas tentativas").
      await qc.cancelQueries();
      qc.setQueryData(SESSION_KEY, null);
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== SESSION_KEY[0] });
    }
  }, [qc]);

  const value = useMemo<SessionState>(
    () => ({ session, loading: query.isPending, error: query.error, setSession, refresh, logout }),
    [session, query.isPending, query.error, setSession, refresh, logout],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components -- hook do provider definido neste módulo
export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession fora do SessionProvider');
  return ctx;
}

/** Sessão garantida (dentro de rotas protegidas). */
// eslint-disable-next-line react-refresh/only-export-components -- hook do provider definido neste módulo
export function useAuthed(): SessionDto {
  const { session } = useSession();
  if (!session) throw new Error('Sessão ausente');
  return session;
}
